import { Box, Typography } from '@mui/material';
import { formatVisibility } from '../../utils/visibilityFormatters';

interface VisibilityLegendProps {
  /**
   * Layout orientation
   * @default 'horizontal'
   */
  orientation?: 'horizontal' | 'vertical';
  /**
   * Whether to show the "Out of hours" legend item
   * @default false
   */
  showOutOfHours?: boolean;
}

const FOG_800M_COLOR = "#d32f2f"; // Red for visibility ≤ 800m
const FOG_1600M_COLOR = "#f57c00"; // Orange for visibility ≤ 1600m
const FOG_3000M_COLOR = "#ffd54f"; // Yellow for visibility ≤ 3000m
const NO_FOG_COLOR = "#4caf50"; // Green for no fog
const NO_DATA_COLOR = "#757575"; // Gray for no data
const OUT_OF_HOURS_COLOR = "#4a4a4a"; // Dark gray for out of hours

export const VisibilityLegend: React.FC<VisibilityLegendProps> = ({
  orientation = 'horizontal',
  showOutOfHours = false
}) => {
  const isHorizontal = orientation === 'horizontal';

  return (
    <Box
      sx={{
        width: '100%',
        backgroundColor: 'rgba(50, 50, 50, 0.5)',
        borderRadius: 1,
        px: 1,
        py: { xs: 1, sm: 1 },
        mb: 2
      }}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: isHorizontal ? 'row' : 'column',
          alignItems: 'center',
          gap: { xs: 2, sm: 4 },
          flexWrap: 'wrap'
        }}
      >
        <Typography
          variant="body2"
          sx={{
            fontWeight: 700,
            fontSize: { xs: '0.8rem', sm: '0.9rem' },
            color: 'text.primary',
            mr: isHorizontal ? 1 : 0
          }}
        >
          Visibility Legend
        </Typography>

        <Box
          sx={{
            display: 'flex',
            flexDirection: isHorizontal ? 'row' : 'column',
            gap: { xs: 2, sm: 3 },
            flexWrap: 'wrap'
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
            <Box
              sx={{
                width: 16,
                height: 16,
                backgroundColor: FOG_800M_COLOR,
                borderRadius: 0.5,
                flexShrink: 0,
                border: '1px solid rgba(255, 255, 255, 0.2)'
              }}
            />
            <Typography
              variant="caption"
              sx={{
                fontSize: { xs: '0.7rem', sm: '0.75rem' },
                color: 'text.primary',
                opacity: 0.9
              }}
            >
              {`≤ ${formatVisibility(800)}`}
            </Typography>
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
            <Box
              sx={{
                width: 16,
                height: 16,
                backgroundColor: FOG_1600M_COLOR,
                borderRadius: 0.5,
                flexShrink: 0,
                border: '1px solid rgba(255, 255, 255, 0.2)'
              }}
            />
            <Typography
              variant="caption"
              sx={{
                fontSize: { xs: '0.7rem', sm: '0.75rem' },
                color: 'text.primary',
                opacity: 0.9
              }}
            >
              {`≤ ${formatVisibility(1600)}`}
            </Typography>
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
            <Box
              sx={{
                width: 16,
                height: 16,
                backgroundColor: FOG_3000M_COLOR,
                borderRadius: 0.5,
                flexShrink: 0,
                border: '1px solid rgba(255, 255, 255, 0.2)'
              }}
            />
            <Typography
              variant="caption"
              sx={{
                fontSize: { xs: '0.7rem', sm: '0.75rem' },
                color: 'text.primary',
                opacity: 0.9
              }}
            >
              {`≤ ${formatVisibility(3000)}`}
            </Typography>
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
            <Box
              sx={{
                width: 16,
                height: 16,
                backgroundColor: NO_FOG_COLOR,
                borderRadius: 0.5,
                flexShrink: 0,
                border: '1px solid rgba(255, 255, 255, 0.2)'
              }}
            />
            <Typography
              variant="caption"
              sx={{
                fontSize: { xs: '0.7rem', sm: '0.75rem' },
                color: 'text.primary',
                opacity: 0.9
              }}
            >
              {`> ${formatVisibility(3000)}`}
            </Typography>
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
            <Box
              sx={{
                width: 16,
                height: 16,
                backgroundColor: NO_DATA_COLOR,
                borderRadius: 0.5,
                flexShrink: 0,
                border: '1px solid rgba(255, 255, 255, 0.2)'
              }}
            />
            <Typography
              variant="caption"
              sx={{
                fontSize: { xs: '0.7rem', sm: '0.75rem' },
                color: 'text.primary',
                opacity: 0.9
              }}
            >
              No data
            </Typography>
          </Box>

          {showOutOfHours && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
              <Box
                sx={{
                  width: 16,
                  height: 16,
                  backgroundColor: OUT_OF_HOURS_COLOR,
                  borderRadius: 0.5,
                  flexShrink: 0,
                  border: '1px solid rgba(255, 255, 255, 0.2)',
                  backgroundImage: `repeating-linear-gradient(
                    45deg,
                    transparent,
                    transparent 2px,
                    #666666 2px,
                    #666666 4px
                  )`
                }}
              />
              <Typography
                variant="caption"
                sx={{
                  fontSize: { xs: '0.7rem', sm: '0.75rem' },
                  color: 'text.primary',
                  opacity: 0.9
                }}
              >
                Out of operational hours
              </Typography>
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  );
};
